import { useEffect, useState } from "react";
import { FONT, COLOR } from "../lib/tokens";
import { supabase } from "../lib/supabase";

// TripHistoryModal — lists every committed shopping trip the viewer
// can see (self + family via shopping_trips RLS). Companion to
// ReceiptHistoryModal: that one is keyed on receipts, this one is
// keyed on trips so trips committed WITHOUT a receipt are reachable
// for after-the-fact provenance.
//
// Each trip row is a navigation tap — opens TripDetailModal where
// the user can see the items scanned in that session and either
// view the attached receipt or attach one. Status pill indicates
// receipt presence at a glance so the user can scan the list.
//
// Grouping mirrors ReceiptHistoryModal — by month, with a row count
// header. No pagination yet (500-row hard cap).
//
// Props:
//   userId               — viewer id, for "mine" vs "FAMILY" tagging
//   refreshKey           — bump to force a re-fetch (e.g. after the
//                          detail screen attaches a receipt)
//   onOpenTrip(tripId)   — tap handler; parent opens TripDetailModal
//   onClose()            — dismiss
export default function TripHistoryModal({ userId, refreshKey = 0, onOpenTrip, onClose }) {
  const [rows, setRows] = useState(null); // null while loading
  const [error, setError] = useState(null);

  // Close on Escape — same keyboard contract as ReceiptHistoryModal.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    let alive = true;
    (async () => {
      // Only checked-out trips are interesting here. Active trips are
      // handled by useShopTrip; cancelled ones never closed properly.
      const { data, error: err } = await supabase
        .from("shopping_trips")
        .select("id, user_id, store_name, started_at, ended_at, receipt_id, status, created_at")
        .eq("status", "checked_out")
        .order("ended_at", { ascending: false, nullsFirst: false })
        .limit(500);
      if (!alive) return;
      if (err) {
        console.warn("[trips] history load failed:", err.message);
        setError(err.message);
        setRows([]);
        return;
      }
      const trips = data || [];

      // Per-trip scan count. One round-trip — pull all trip_scans for
      // the listed trip ids and group client-side. Cheaper than an
      // RPC and avoids a count-each-trip N+1.
      const ids = trips.map((t) => t.id);
      const counts = new Map();
      if (ids.length > 0) {
        const { data: scanRows } = await supabase
          .from("trip_scans")
          .select("trip_id")
          .in("trip_id", ids);
        for (const r of scanRows || []) {
          counts.set(r.trip_id, (counts.get(r.trip_id) || 0) + 1);
        }
      }
      if (!alive) return;
      setRows(trips.map((t) => ({ ...t, _scanCount: counts.get(t.id) || 0 })));
    })();
    return () => { alive = false; };
  }, [refreshKey]);

  // Group trips by month for navigable scrolling. Anchor on ended_at
  // (when the trip was committed); fall back to created_at.
  const grouped = (() => {
    if (!rows) return null;
    const acc = new Map();
    for (const t of rows) {
      const anchor = t.ended_at || t.created_at;
      const d = new Date(anchor);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      if (!acc.has(key)) {
        acc.set(key, {
          label: d.toLocaleDateString(undefined, { month: "long", year: "numeric" }),
          items: [],
          withReceipt: 0,
        });
      }
      const bucket = acc.get(key);
      bucket.items.push(t);
      if (t.receipt_id) bucket.withReceipt += 1;
    }
    return Array.from(acc.entries()).sort((a, b) => b[0].localeCompare(a[0]));
  })();

  return (
    <div style={{
      position: "fixed", inset: 0, background: "#000000dd", zIndex: 316,
      display: "flex", alignItems: "flex-end",
      maxWidth: 480, margin: "0 auto",
    }}>
      <div style={{
        width: "100%", background: COLOR.ground,
        borderRadius: "20px 20px 0 0", padding: "18px 22px 36px",
        maxHeight: "92vh", overflowY: "auto",
        position: "relative",
      }}>
        <div style={{ width: 44, height: 4, background: COLOR.border, borderRadius: 2, margin: "0 auto 14px" }} />

        <button
          onClick={onClose}
          aria-label="Close"
          style={{
            position: "absolute", top: 12, right: 14,
            width: 32, height: 32,
            background: COLOR.deep, border: `1px solid ${COLOR.border}`,
            color: COLOR.dim, borderRadius: 16,
            fontFamily: FONT.mono, fontSize: 14,
            cursor: "pointer", zIndex: 1,
          }}
        >
          ✕
        </button>

        <div style={{ fontFamily: FONT.mono, fontSize: 10, color: COLOR.leaf, letterSpacing: "0.12em", marginBottom: 6 }}>
          TRIP HISTORY
        </div>
        <h2 style={{ fontFamily: FONT.serif, fontSize: 22, fontStyle: "italic", color: COLOR.ink, fontWeight: 500, margin: 0, lineHeight: 1.2, marginBottom: 14 }}>
          Every shopping trip you've committed
        </h2>

        {error && (
          <div style={{
            padding: "10px 12px", marginBottom: 12,
            background: "#3a1a1a", border: `1px solid ${COLOR.border}`, borderRadius: 8,
            fontFamily: FONT.mono, fontSize: 11, color: "#ff9b9b",
          }}>
            {error}
          </div>
        )}

        {rows === null && (
          <div style={{ padding: "40px 12px", textAlign: "center", fontFamily: FONT.mono, fontSize: 11, color: COLOR.sub, letterSpacing: "0.1em" }}>
            LOADING…
          </div>
        )}

        {rows && rows.length === 0 && (
          <div style={{
            padding: "28px 16px", textAlign: "center",
            background: COLOR.deep, border: `1px dashed ${COLOR.edge}`, borderRadius: 10,
            fontFamily: FONT.sans, fontSize: 12, color: COLOR.muted, fontStyle: "italic",
          }}>
            No committed trips yet. Once you check out from Shop Mode they'll show up here.
          </div>
        )}

        {grouped && grouped.map(([key, bucket]) => (
          <div key={key} style={{ marginBottom: 18 }}>
            <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 8 }}>
              <div style={{ fontFamily: FONT.mono, fontSize: 10, color: COLOR.sky, letterSpacing: "0.12em" }}>
                {bucket.label.toUpperCase()} · {bucket.items.length} TRIP{bucket.items.length === 1 ? "" : "S"}
              </div>
              <div style={{ fontFamily: FONT.mono, fontSize: 10, color: COLOR.leafMuted, letterSpacing: "0.04em" }}>
                {bucket.withReceipt}/{bucket.items.length} W/ RECEIPT
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {bucket.items.map((t) => {
                const dateLabel = (() => {
                  const a = t.ended_at || t.created_at;
                  return a
                    ? new Date(a).toLocaleDateString(undefined, { month: "short", day: "numeric" })
                    : "—";
                })();
                const isMine = t.user_id === userId;
                const hasReceipt = !!t.receipt_id;
                const pillBg = hasReceipt ? COLOR.deep : "#2a3a1f";
                const pillFg = hasReceipt ? COLOR.leafMuted : COLOR.leaf;
                const pillLabel = hasReceipt ? "RECEIPT" : "NO RECEIPT";
                return (
                  <button
                    key={t.id}
                    onClick={() => onOpenTrip?.(t.id)}
                    style={{
                      display: "flex", alignItems: "center", gap: 10,
                      padding: "10px 12px",
                      background: COLOR.ground, border: `1px solid ${COLOR.edge}`,
                      borderRadius: 10, cursor: "pointer", textAlign: "left",
                      touchAction: "manipulation",
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontFamily: FONT.serif, fontSize: 14, color: COLOR.ink, fontStyle: "italic", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {t.store_name || "Unknown store"}
                      </div>
                      <div style={{ fontFamily: FONT.mono, fontSize: 9, color: COLOR.sub, marginTop: 2, letterSpacing: "0.05em" }}>
                        {dateLabel.toUpperCase()}
                        {t._scanCount != null ? ` · ${t._scanCount} ITEM${t._scanCount === 1 ? "" : "S"}` : ""}
                        {!isMine ? " · FAMILY" : ""}
                      </div>
                    </div>
                    <span style={{
                      fontFamily: FONT.mono, fontSize: 9,
                      letterSpacing: "0.08em",
                      padding: "4px 8px",
                      background: pillBg, color: pillFg,
                      border: `1px solid ${COLOR.edge}`,
                      borderRadius: 4,
                      whiteSpace: "nowrap",
                    }}>
                      {pillLabel}
                    </span>
                    <span style={{ fontFamily: FONT.mono, fontSize: 10, color: COLOR.sky, letterSpacing: "0.08em", marginLeft: 4 }}>→</span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
